import { AnimatePresence } from 'framer-motion'
import usePages from 'hooks/usePages'
import React, { Suspense, useEffect } from 'react'
import { Route, Routes, useLocation } from 'react-router-dom'
import BaseLayout from './layouts/BaseLayout'
import {
  NotFoundPage,
  DynamicPage,
  HomePage,
  SearchPage,
  SpeciesPage,
  ContactPage,
  AwardPage,
  GalleryPage,
  BreedingRecordPage,
  NewsEventsPage,
  JoinPage,
  AccountDetailsPage,
  AccountAdvertsPage,
  AccountAdvertFormPage,
  UsefulLinksPage,
  AdvertPage,
} from './pages'
import useAuth from 'hooks/useAuth'
import { useDispatch, useSelector } from 'react-redux'
import { setUser } from './redux/stateSlice'
import useFetch from 'hooks/useFetch'
import { Toaster } from 'react-hot-toast'
import { Navigate, useParams } from 'react-router-dom'

const SubscriberRoute = ({ element }) => {
  const { currentUser } = useSelector(state => state.state)
  if (currentUser === null) return null
  if (currentUser === false || !currentUser.hasActiveSubscription) return <Navigate to="/join" replace />
  return element
}

const SectionRoute = () => {
  const { section } = useParams()
  const { currentUser } = useSelector(state => state.state)
  if (section === 'members') {
    if (currentUser === null) return null
    if (currentUser === false || !currentUser.hasActiveSubscription) return <Navigate to="/join" replace />
  }
  return <DynamicPage />
}

const AppRoutes = () => {
  const location = useLocation()
  const { pages, findOne: findPage } = usePages()
  const fetch = useFetch()
  const auth = useAuth()
  const dispatch = useDispatch()

  const homeProps = findPage({ uri: '__home__' })
  const contactProps = findPage({ slug: 'contact' })
  const awardsProps = findPage({ slug: 'awards' })
  const galleryProps = findPage({ slug: 'gallery' })
  const newsEventsProps = findPage({ slug: 'news-events' })
  const breedingRecordsProps = findPage({ slug: 'breeding-records' })
  const speciesIndexProps = findPage({ slug: 'species-index' })
  const magazinesProps = findPage({ slug: 'magazines' })
  const advertsProps = findPage({ slug: 'adverts' })
  const joinProps = findPage({ slug: 'join' })

  const searchPages = [
    { ...speciesIndexProps, uri: '/species-index', index: 'species', title: 'Species Index', resetSearch: true },
    { ...breedingRecordsProps, uri: '/breeding-records', index: 'breedingRecords', title: 'First UK Breeding Records', resetSearch: true },
    { ...magazinesProps, uri: '/magazines', title: 'Magazine Archive', index: 'magazines', resetSearch: true },
    { ...advertsProps, uri: '/members/adverts', title: 'Adverts', index: 'adverts', bodyClass: 'listings', resetSearch: true, types: ['all', 'listing', 'event', 'notice'] },
    { uri: '/search', title: 'Search' },
  ]

  useEffect(() => {
    fetch.cancelAll()

    auth.check(res => dispatch(setUser(res)))

    document.documentElement.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth', // Optional if you want to skip the scrolling animation
    })
  }, [location.pathname])

  return (
    <Suspense fallback={null}>
      <Toaster
        position="bottom-right"
        toastOptions={{
          style: {
            fontFamily: 'Intro',
          },
          success: {
            style: {
              border: '1px solid #1abf67',
              color: '#1abf67',
            },
          },
          error: {
            style: {
              border: '1px solid red',
            },
          },
        }}
      />
      <AnimatePresence mode="wait">
        <Routes key={location.pathname} location={location}>
          <Route path="/" element={<BaseLayout />}>
            {/* Singles */}
            <Route index element={<HomePage {...homeProps} />} />
            <Route path="/contact" element={<ContactPage {...contactProps} />} />

            <Route path="/awards" element={<AwardPage title="Awards" {...awardsProps} />} />

            <Route path="/gallery" element={<GalleryPage title="Gallery" {...galleryProps} />} />

            <Route path="species/:slug" element={<SpeciesPage />} />

            <Route path="breeding-records/:slug" element={<BreedingRecordPage />} />
            {/* Section Entry routes */}
            <Route path=":section/:slug" element={<SectionRoute />} />

            <Route path="/join" element={<JoinPage {...joinProps} />} />

            <Route path="/account" element={<AccountDetailsPage />} />
            <Route path="/members/adverts/:id" element={<SubscriberRoute element={<AdvertPage />} />} />
            <Route path="/account/adverts" element={<SubscriberRoute element={<AccountAdvertsPage />} />} />
            <Route path="/account/adverts/new" element={<SubscriberRoute element={<AccountAdvertFormPage />} />} />
            <Route path="/account/adverts/:id/edit" element={<SubscriberRoute element={<AccountAdvertFormPage />} />} />
            {/* <Route path="/members/useful-links" element={<SubscriberRoute element={<UsefulLinksPage />} />} /> */}
            <Route path="/useful-links" element={<UsefulLinksPage />} />

            <Route path="/news" element={<NewsEventsPage {...newsEventsProps} uri={'/news-events'} title={'News'} index={'news'} />} />
            <Route path="/events" element={<NewsEventsPage {...newsEventsProps} uri={'/news-events'} title={'Events'} index={'events'} />} />
            <Route path="/minutes" element={<NewsEventsPage {...newsEventsProps} uri={'/news-events'} title={'Meetings Minutes'} index={'minutes'} />} />

            {searchPages.map((page, index) => {
              const el = <SearchPage {...page} />
              return <Route key={index} path={page.uri} element={page.uri === '/members/adverts' ? <SubscriberRoute element={el} /> : el} />
            })}
            {/* Page Entry routes */}
            {pages.map((page, index) => {
              return <Route key={index} path={page.uri} element={<DynamicPage {...page} />} />
            })}

            {/* 404 Fallback */}
            <Route path="*" element={<NotFoundPage />} status={404} />
          </Route>
        </Routes>
      </AnimatePresence>
    </Suspense>
  )
}

export default AppRoutes
